import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

import { isDemoBrand, demoHash } from '../../utils/demoBrand';

const API = 'https://api.glowytics.com/api/pinterest';

// Static Pinterest dashboard datasets for demo brands. ctr/cpc/cpm derived from
// spend/clicks/impressions; conversions_value stands in for revenue.
function buildDemoPinterest() {
  const today = new Date();
  const daily = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const ds = d.toISOString().slice(0, 10);
    const h = demoHash('pin' + ds);
    const impressions = 50000 + (h % 150000);
    const clicks = Math.round(impressions * (0.005 + (h % 15) / 1000));
    const spend = Math.round(impressions * (0.7 + (h % 50) / 100) / 100);
    const conversions = Math.round(clicks * (0.02 + (h % 20) / 1000));
    daily.push({
      date: ds, spend, impressions, clicks, conversions,
      reach: Math.round(impressions / (1.4 + (h % 8) / 10)),
      video_views: Math.round(impressions * (0.2 + (h % 30) / 100)),
      conversions_value: Math.round(conversions * (450 + (h % 600))),
    });
  }
  const campNames = ['Consideration - Fashion', 'Conversions - Retargeting', 'Video Views - Brand', 'Traffic - Prospecting', 'Catalog Sales - DPA'];
  const campaigns = campNames.map((n, i) => {
    const h = demoHash('pincamp' + n);
    const impressions = 300000 + (h % 1500000);
    const clicks = Math.round(impressions * (0.005 + (h % 15) / 1000));
    const spend = Math.round(impressions * (0.7 + (h % 50) / 100) / 100);
    const conversions = Math.round(clicks * (0.02 + (h % 20) / 1000));
    return {
      id: `demo-pin-${i}`, name: n, spend, impressions, clicks, conversions,
      reach: Math.round(impressions / (1.4 + (h % 8) / 10)),
      video_views: Math.round(impressions * (0.2 + (h % 30) / 100)),
      conversions_value: Math.round(conversions * (450 + (h % 600))),
    };
  });
  const sum = (arr, f) => arr.reduce((s, r) => s + (Number(r[f]) || 0), 0);
  const tImp = sum(daily, 'impressions'), tClk = sum(daily, 'clicks'), tSpend = sum(daily, 'spend');
  const totals = {
    spend: tSpend, impressions: tImp, reach: sum(daily, 'reach'), video_views: sum(daily, 'video_views'),
    clicks: tClk, conversions: sum(daily, 'conversions'), conversions_value: sum(daily, 'conversions_value'),
    ctr: tImp > 0 ? tClk / tImp : 0, cpc: tClk > 0 ? tSpend / tClk : 0, cpm: tImp > 0 ? (tSpend / tImp) * 1000 : 0,
  };
  return { totals, daily, campaigns };
}

const hdrs = () => ({
  'Content-Type': 'application/json',
  'X-Brand-ID': localStorage.getItem('currentBrandId') || '',
});
const opts = () => ({ credentials: 'include', headers: hdrs() });

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('en-US');
const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;
const fmtShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toFixed(n < 10 ? 2 : 0);
};

const PERIODS = [
  { key: '7', label: 'Last 7 Days' },
  { key: '14', label: 'Last 14 Days' },
  { key: '30', label: 'Last 30 Days' },
  { key: '90', label: 'Last 90 Days' },
  { key: 'custom', label: 'Custom' },
];

export default function PinterestDashboard() {
  const [period, setPeriod] = useState('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [chartMetric, setChartMetric] = useState('spend');
  const [conn, setConn] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/connection`, opts());
        const d = await r.json();
        setConn(d);
      } catch (_) {}
    })();
  }, []);

  const dateRange = useMemo(() => {
    if (period === 'custom') return { startDate: customFrom, endDate: customTo };
    const days = Number(period);
    const end = new Date();
    const start = new Date(Date.now() - days * 86400000);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, [period, customFrom, customTo]);

  const fetchData = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(
        `${API}/db/stats?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        opts()
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const demo = isDemoBrand(localStorage.getItem('currentBrandId')) && !j?.campaigns?.length && !j?.daily?.length;
      setData(demo ? buildDemoPinterest() : j);
    } catch (e) {
      if (isDemoBrand(localStorage.getItem('currentBrandId'))) { setData(buildDemoPinterest()); setError(''); }
      else setError(e.message);
    }
    setLoading(false);
  }, [dateRange, period, customFrom, customTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const daily = useMemo(() => data?.daily || [], [data]);
  const campaigns = useMemo(() => data?.campaigns || [], [data]);
  const totals = useMemo(() => data?.totals || {}, [data]);
  const chartData = useMemo(() => [...daily].reverse(), [daily]);

  const kpis = useMemo(() => [
    { label: 'Spend', value: fmtMoney(totals.spend), sub: 'Total ad spend', color: '#E60023' },
    { label: 'Impressions', value: fmt(totals.impressions), sub: 'Total views', color: '#0076D3' },
    { label: 'Reach', value: fmt(totals.reach), sub: 'Unique users reached', color: '#0EA5E9' },
    { label: 'Video Views', value: fmt(totals.video_views), sub: '50% video views', color: '#A855F7' },
    { label: 'Clicks', value: fmt(totals.clicks), sub: 'Total clicks', color: '#00A86B' },
    { label: 'CTR', value: fmtPct(totals.ctr), sub: 'Click-through rate', color: '#8B5CF6' },
    { label: 'CPC', value: fmtMoney(totals.cpc), sub: 'Cost per click', color: '#F59E0B' },
    { label: 'CPM', value: fmtMoney(totals.cpm), sub: 'Cost per 1K imp.', color: '#6366F1' },
    { label: 'Conversions', value: fmt(totals.conversions), sub: 'Total conversions', color: '#10B981' },
    { label: 'Conv. Value', value: fmtMoney(totals.conversions_value), sub: 'Revenue from ads', color: '#EC4899' },
  ], [totals]);

  const chartMetrics = [
    { key: 'spend', label: 'Spend', color: '#E60023' },
    { key: 'impressions', label: 'Impressions', color: '#0076D3' },
    { key: 'reach', label: 'Reach', color: '#0EA5E9' },
    { key: 'video_views', label: 'Video Views', color: '#A855F7' },
    { key: 'clicks', label: 'Clicks', color: '#00A86B' },
    { key: 'conversions', label: 'Conversions', color: '#10B981' },
  ];

  const exportCSV = () => {
    if (!daily.length) return;
    const headers = ['Date', 'Spend', 'Impressions', 'Reach', 'Video Views', 'Clicks', 'CTR', 'CPC', 'CPM', 'Conversions', 'Conv Value'];
    const rows = daily.map(d => [
      d.date,
      Number(d.spend || 0).toFixed(2),
      d.impressions || 0,
      d.reach || 0,
      d.video_views || 0,
      d.clicks || 0,
      d.impressions > 0 ? ((d.clicks / d.impressions) * 100).toFixed(2) + '%' : '0%',
      d.clicks > 0 ? (d.spend / d.clicks).toFixed(2) : '0',
      d.impressions > 0 ? ((d.spend / d.impressions) * 1000).toFixed(2) : '0',
      d.conversions || 0,
      Number(d.conversions_value || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pinterest_analytics_${dateRange.startDate}_${dateRange.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-[1400px] mx-auto space-y-5">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/pinterestlogopng.png" alt="Pinterest" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Pinterest Ads</h1>
                <p className="text-sm text-gray-500">
                  {conn?.accountName && <span className="font-medium text-gray-700">{conn.accountName}</span>}
                  {conn?.adAccountId && <span className="text-gray-400"> · {conn.adAccountId}</span>}
                  {dateRange.startDate && dateRange.endDate && (
                    <span className="text-gray-400"> · {dateRange.startDate} → {dateRange.endDate}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {PERIODS.map(p => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    period === p.key
                      ? 'bg-[#E60023] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button onClick={fetchData} disabled={loading}
                className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all disabled:opacity-50"
                title="Refresh">
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button onClick={exportCSV}
                className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all"
                title="Export CSV">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            </div>
          </div>

          {period === 'custom' && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#E60023]/30 focus:border-[#E60023] outline-none" />
              <span className="text-gray-400 text-sm">→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#E60023]/30 focus:border-[#E60023] outline-none" />
              <button onClick={fetchData} className="px-4 py-1.5 bg-[#E60023] text-white text-sm font-medium rounded-lg hover:bg-[#C5001F] transition-colors">
                Apply
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-10 gap-3">
          {kpis.map(k => (
            <div key={k.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: k.color }} />
                <span className="text-xs text-gray-500 font-medium">{k.label}</span>
              </div>
              <div className="text-lg font-bold text-gray-900 leading-tight">{k.value}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Daily Performance</h2>
            <div className="flex gap-1">
              {chartMetrics.map(m => (
                <button
                  key={m.key}
                  onClick={() => setChartMetric(m.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    chartMetric === m.key
                      ? 'text-white shadow-sm'
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                  }`}
                  style={chartMetric === m.key ? { backgroundColor: m.color } : {}}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[280px]">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="pinterestGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartMetrics.find(m => m.key === chartMetric)?.color} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={chartMetrics.find(m => m.key === chartMetric)?.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v?.slice(5)} stroke="#ccc" />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtShort} stroke="#ccc" width={50} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                    formatter={(v) => [chartMetric === 'spend' ? fmtMoney(v) : fmt(v), chartMetrics.find(m => m.key === chartMetric)?.label]}
                    labelFormatter={l => l}
                  />
                  <Area
                    type="monotone"
                    dataKey={chartMetric}
                    stroke={chartMetrics.find(m => m.key === chartMetric)?.color}
                    strokeWidth={2}
                    fill="url(#pinterestGrad)"
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                {loading ? 'Loading chart data...' : 'No data available for selected period'}
              </div>
            )}
          </div>
        </div>

        {/* Campaign Breakdown */}
        {campaigns.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <img src="/pinterestlogopng.png" alt="" className="w-4 h-4 object-contain" />
              <h2 className="text-sm font-semibold text-gray-800">Campaign Breakdown</h2>
              <span className="text-xs text-gray-400 ml-1">{campaigns.length} campaigns</span>
            </div>

            {/* Campaign Bar Chart */}
            <div className="h-[200px] mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={campaigns.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmtShort(v)} stroke="#ccc" />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={180} stroke="#ccc"
                    tickFormatter={v => v?.length > 30 ? v.slice(0, 28) + '…' : v} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                    formatter={(v) => [fmtMoney(v), 'Spend']}
                  />
                  <Bar dataKey="spend" fill="#E60023" radius={[0, 4, 4, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Campaign Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 px-3 text-gray-500 font-medium">Campaign</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Spend</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Impressions</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Reach</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Video Views</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Clicks</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">CTR</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">CPC</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Conv.</th>
                    <th className="text-right py-2 px-3 text-gray-500 font-medium">Conv. Value</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c, i) => (
                    <tr key={c.id} className={`border-b border-gray-50 hover:bg-red-50/30 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                      <td className="py-2 px-3 font-medium text-gray-800 max-w-[300px] truncate">{c.name}</td>
                      <td className="py-2 px-3 text-right text-red-600 font-medium">{fmtMoney(c.spend)}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{fmt(c.impressions)}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{fmt(c.reach)}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{fmt(c.video_views)}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{fmt(c.clicks)}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{c.impressions > 0 ? fmtPct(c.clicks / c.impressions) : '-'}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{c.clicks > 0 ? fmtMoney(c.spend / c.clicks) : '-'}</td>
                      <td className="py-2 px-3 text-right text-gray-700">{fmt(c.conversions)}</td>
                      <td className="py-2 px-3 text-right text-green-600 font-medium">{fmtMoney(c.conversions_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Daily Breakdown Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Daily Breakdown</h2>
            <span className="text-xs text-gray-400">{daily.length} days</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">Date</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Spend</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Impressions</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Reach</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Video Views</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Clicks</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">CTR</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">CPC</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">CPM</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Conv.</th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">Conv. Value</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((d, i) => (
                  <tr key={d.date} className={`border-b border-gray-50 hover:bg-red-50/30 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                    <td className="py-2 px-3 font-medium text-gray-800">{d.date}</td>
                    <td className="py-2 px-3 text-right text-red-600 font-medium">{fmtMoney(d.spend)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{fmt(d.impressions)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{fmt(d.reach)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{fmt(d.video_views)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{fmt(d.clicks)}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{d.impressions > 0 ? fmtPct(d.clicks / d.impressions) : '-'}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{d.clicks > 0 ? fmtMoney(d.spend / d.clicks) : '-'}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{d.impressions > 0 ? fmtMoney((d.spend / d.impressions) * 1000) : '-'}</td>
                    <td className="py-2 px-3 text-right text-gray-700">{fmt(d.conversions)}</td>
                    <td className="py-2 px-3 text-right text-green-600 font-medium">{fmtMoney(d.conversions_value)}</td>
                  </tr>
                ))}
                {daily.length > 0 && (
                  <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                    <td className="py-2 px-3 text-gray-800">Total</td>
                    <td className="py-2 px-3 text-right text-red-600">{fmtMoney(totals.spend)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmt(totals.impressions)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmt(totals.reach)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmt(totals.video_views)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmt(totals.clicks)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmtPct(totals.ctr)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmtMoney(totals.cpc)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmtMoney(totals.cpm)}</td>
                    <td className="py-2 px-3 text-right text-gray-800">{fmt(totals.conversions)}</td>
                    <td className="py-2 px-3 text-right text-green-600">{fmtMoney(totals.conversions_value)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
